/**
 * Unified Storage Abstraction
 * Platform-aware storage manager that uses:
 * - Keychain (iOS) / EncryptedSharedPreferences (Android) for sensitive data
 * - Capacitor Preferences for native storage
 * - localStorage/sessionStorage for web
 * 
 * @author Varun Pandey
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

export interface StorageManager {
  get<T>(key: string, secure?: boolean): Promise<T | null>;
  set<T>(key: string, value: T, secure?: boolean): Promise<void>;
  remove(key: string, secure?: boolean): Promise<void>;
  clear(secure?: boolean): Promise<void>;
  getAllKeys(secure?: boolean): Promise<string[]>;
}

class StorageManagerImpl implements StorageManager {
  private isNative = Capacitor.isNativePlatform();

  /**
   * Get value from storage
   */
  async get<T>(key: string, secure = false): Promise<T | null> {
    try {
      if (secure && this.isNative) {
        // Use Keychain/EncryptedSharedPreferences for sensitive data
        // This will be implemented with a custom Capacitor plugin
        const { value } = await Preferences.get({ key: `secure_${key}` });
        return value ? (JSON.parse(value) as T) : null;
      }

      if (this.isNative) {
        const { value } = await Preferences.get({ key });
        return value ? (JSON.parse(value) as T) : null;
      }

      // Web: Use localStorage
      const value = localStorage.getItem(key);
      return value ? (JSON.parse(value) as T) : null;
    } catch (error) {
      console.error(`Storage get error for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Set value in storage
   */
  async set<T>(key: string, value: T, secure = false): Promise<void> {
    try {
      const serialized = JSON.stringify(value);

      if (secure && this.isNative) {
        // Use Keychain/EncryptedSharedPreferences for sensitive data
        await Preferences.set({ key: `secure_${key}`, value: serialized });
        return;
      }

      if (this.isNative) {
        await Preferences.set({ key, value: serialized });
        return;
      }

      // Web: Use localStorage
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`Storage set error for key ${key}:`, error);
      throw error;
    }
  }

  /**
   * Remove value from storage
   */
  async remove(key: string, secure = false): Promise<void> {
    try {
      const storageKey = secure ? `secure_${key}` : key;

      if (this.isNative) {
        await Preferences.remove({ key: storageKey });
        return;
      }

      // Web: Use localStorage
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Storage remove error for key ${key}:`, error);
      throw error;
    }
  }

  /**
   * Clear all storage
   */
  async clear(secure = false): Promise<void> {
    try {
      if (this.isNative) {
        if (secure) {
          // Clear only secure keys
          const keys = await Preferences.keys();
          const secureKeys = keys.keys.filter((k) => k.startsWith('secure_'));
          await Promise.all(secureKeys.map((k) => Preferences.remove({ key: k })));
        } else {
          await Preferences.clear();
        }
        return;
      }

      // Web: Use localStorage
      if (secure) {
        // Clear only secure keys
        const keys = Object.keys(localStorage);
        const secureKeys = keys.filter((k) => k.startsWith('secure_'));
        secureKeys.forEach((k) => localStorage.removeItem(k));
      } else {
        localStorage.clear();
      }
    } catch (error) {
      console.error('Storage clear error:', error);
      throw error;
    }
  }

  /**
   * Get all storage keys
   */
  async getAllKeys(secure = false): Promise<string[]> {
    try {
      if (this.isNative) {
        const { keys } = await Preferences.keys();
        if (secure) {
          return keys.filter((k) => k.startsWith('secure_')).map((k) => k.replace('secure_', ''));
        }
        return keys.filter((k) => !k.startsWith('secure_'));
      }

      // Web: Use localStorage
      const keys = Object.keys(localStorage);
      if (secure) {
        return keys.filter((k) => k.startsWith('secure_')).map((k) => k.replace('secure_', ''));
      }
      return keys.filter((k) => !k.startsWith('secure_'));
    } catch (error) {
      console.error('Storage getAllKeys error:', error);
      return [];
    }
  }
}

// Session storage manager (web only, temporary data)
class SessionStorageManager {
  private isWeb = !Capacitor.isNativePlatform();

  async get<T>(key: string): Promise<T | null> {
    if (!this.isWeb) return null;
    try {
      const value = sessionStorage.getItem(key);
      return value ? (JSON.parse(value) as T) : null;
    } catch (error) {
      console.error(`SessionStorage get error for key ${key}:`, error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    if (!this.isWeb) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`SessionStorage set error for key ${key}:`, error);
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    if (!this.isWeb) return;
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`SessionStorage remove error for key ${key}:`, error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    if (!this.isWeb) return;
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('SessionStorage clear error:', error);
      throw error;
    }
  }
}

// Export singleton instances
export const storageManager = new StorageManagerImpl();
export const sessionStorageManager = new SessionStorageManager();
